import { useState, useMemo, useCallback, useRef } from "react";

// ─── TOOL DEFINITIONS ─────────────────────────────────────────────────────────
const TOOLS = [
  { id:"base64-enc",  label:"Base64 Encode",   icon:"⬆",  group:"Base64",   desc:"Text → Base64" },
  { id:"base64-dec",  label:"Base64 Decode",   icon:"⬇",  group:"Base64",   desc:"Base64 → Text" },
  { id:"base64-img",  label:"Base64 Image",    icon:"🖼",  group:"Base64",   desc:"Preview Base64 images" },
  { id:"url-enc",     label:"URL Encode",      icon:"🔗",  group:"URL",      desc:"Encode for URLs" },
  { id:"url-dec",     label:"URL Decode",      icon:"🔓",  group:"URL",      desc:"Decode URL strings" },
  { id:"html-enc",    label:"HTML Encode",     icon:"&",   group:"HTML",     desc:"Encode HTML entities" },
  { id:"html-dec",    label:"HTML Decode",     icon:"<>",  group:"HTML",     desc:"Decode HTML entities" },
  { id:"jwt",         label:"JWT Decoder",     icon:"🔑",  group:"JWT",      desc:"Decode & inspect tokens" },
  { id:"hex-enc",     label:"Hex Encode",      icon:"0x↑", group:"Hex",      desc:"Text → Hexadecimal" },
  { id:"hex-dec",     label:"Hex Decode",      icon:"0x↓", group:"Hex",      desc:"Hex → Text" },
  { id:"unicode-enc", label:"Unicode Escape",  icon:"\\u", group:"Unicode",  desc:"Escape to \\uXXXX" },
  { id:"unicode-dec", label:"Unicode Unescape",icon:"↩",   group:"Unicode",  desc:"Unescape \\uXXXX" },
  { id:"hash",        label:"Hash Generator",  icon:"#",   group:"Hash",     desc:"MD5 / SHA-256 hash" },
  { id:"morse",       label:"Morse Code",      icon:"•—",  group:"Morse",    desc:"Text ↔ Morse code" },
];

const GROUPS = [...new Set(TOOLS.map(t => t.group))];

// ─── ENCODING LOGIC ───────────────────────────────────────────────────────────

// Base64
const encodeBase64 = text => {
  try { return btoa(unescape(encodeURIComponent(text))); }
  catch (e) { throw new Error("Could not encode — check for invalid characters"); }
};
const decodeBase64 = text => {
  try { return decodeURIComponent(escape(atob(text.trim()))); }
  catch (e) { throw new Error("Invalid Base64 string"); }
};

// URL
const encodeURL = text => {
  try { return encodeURIComponent(text); }
  catch (e) { throw new Error("Could not URL encode"); }
};
const decodeURL = text => {
  try { return decodeURIComponent(text); }
  catch (e) { throw new Error("Invalid URL-encoded string"); }
};

// HTML entities
const HTML_ENCODE_MAP = { "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;", "/":"&#x2F;", "`":"&#x60;", "=":"&#x3D;" };
const encodeHTML = text => text.replace(/[&<>"'`=/]/g, c => HTML_ENCODE_MAP[c] || c);
const decodeHTML = text => {
  // Safe: textarea innerHTML doesn't parse or execute markup/scripts,
  // it only resolves HTML entities — this is the correct browser-native pattern.
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
};

// Hex
const encodeHex = text => {
  try {
    return Array.from(new TextEncoder().encode(text))
      .map(b => b.toString(16).padStart(2,"0"))
      .join(" ");
  } catch (e) { throw new Error("Could not encode to hex"); }
};
const decodeHex = text => {
  try {
    const clean = text.replace(/\s+/g,"");
    if (!/^[0-9a-fA-F]*$/.test(clean) || clean.length % 2 !== 0)
      throw new Error("Invalid hex string");
    const bytes = [];
    for (let i = 0; i < clean.length; i += 2)
      bytes.push(parseInt(clean.substr(i, 2), 16));
    return new TextDecoder().decode(new Uint8Array(bytes));
  } catch (e) { throw new Error(e.message || "Invalid hex string"); }
};

// Unicode
const encodeUnicode = text =>
  Array.from(text).map(c => {
    const cp = c.codePointAt(0);
    return cp > 127 ? `\\u${cp.toString(16).padStart(4,"0")}` : c;
  }).join("");

const decodeUnicode = text => {
  try {
    return text.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    ).replace(/\\U([0-9a-fA-F]{8})/g, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16))
    );
  } catch (e) { throw new Error("Invalid Unicode escape sequence"); }
};

// JWT
const decodeJWT = token => {
  try {
    const parts = token.trim().split(".");
    if (parts.length !== 3) throw new Error("JWT must have exactly 3 parts (header.payload.signature)");
    const decode = part => {
      const base64 = part.replace(/-/g,"+").replace(/_/g,"/");
      const padded = base64 + "=".repeat((4 - base64.length % 4) % 4);
      return JSON.parse(decodeURIComponent(escape(atob(padded))));
    };
    const header  = decode(parts[0]);
    const payload = decode(parts[1]);
    const now = Math.floor(Date.now() / 1000);
    const expired = payload.exp ? payload.exp < now : null;
    return { header, payload, signature: parts[2], expired };
  } catch (e) { throw new Error(e.message || "Invalid JWT token"); }
};

// SHA-256 (using Web Crypto API)
const hashSHA256 = async text => {
  const encoder = new TextEncoder();
  const data    = encoder.encode(text);
  const hashBuf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2,"0")).join("");
};

// MD5 — pure JS implementation (no crypto API needed)
function md5(str) {
  function safeAdd(x, y) { const lsw=(x&0xFFFF)+(y&0xFFFF); const msw=(x>>16)+(y>>16)+(lsw>>16); return (msw<<16)|(lsw&0xFFFF); }
  function bitRotateLeft(num, cnt) { return (num<<cnt)|(num>>>(32-cnt)); }
  function md5cmn(q,a,b,x,s,t) { return safeAdd(bitRotateLeft(safeAdd(safeAdd(a,q),safeAdd(x,t)),s),b); }
  function md5ff(a,b,c,d,x,s,t) { return md5cmn((b&c)|((~b)&d),a,b,x,s,t); }
  function md5gg(a,b,c,d,x,s,t) { return md5cmn((b&d)|(c&(~d)),a,b,x,s,t); }
  function md5hh(a,b,c,d,x,s,t) { return md5cmn(b^c^d,a,b,x,s,t); }
  function md5ii(a,b,c,d,x,s,t) { return md5cmn(c^(b|(~d)),a,b,x,s,t); }
  function strToUTF8Arr(str) {
    const arr=[]; for(let i=0;i<str.length;i++){const c=str.charCodeAt(i);if(c<128)arr.push(c);else if(c<2048){arr.push(192|(c>>6));arr.push(128|(c&63));}else{arr.push(224|(c>>12));arr.push(128|((c>>6)&63));arr.push(128|(c&63));}}
    return arr;
  }
  function strToMD5(str) {
    const utf8=strToUTF8Arr(str); const len=utf8.length; const nblocks=((len+8)>>6)+1; const blks=new Array(nblocks*16).fill(0);
    for(let i=0;i<len;i++) blks[i>>2]|=utf8[i]<<((i%4)*8);
    blks[len>>2]|=0x80<<((len%4)*8); blks[nblocks*16-2]=len*8;
    let a=1732584193,b=-271733879,c=-1732584194,d=271733878;
    for(let i=0;i<nblocks*16;i+=16){
      const aa=a,bb=b,cc=c,dd=d;
      a=md5ff(a,b,c,d,blks[i],7,-680876936);d=md5ff(d,a,b,c,blks[i+1],12,-389564586);c=md5ff(c,d,a,b,blks[i+2],17,606105819);b=md5ff(b,c,d,a,blks[i+3],22,-1044525330);
      a=md5ff(a,b,c,d,blks[i+4],7,-176418897);d=md5ff(d,a,b,c,blks[i+5],12,1200080426);c=md5ff(c,d,a,b,blks[i+6],17,-1473231341);b=md5ff(b,c,d,a,blks[i+7],22,-45705983);
      a=md5ff(a,b,c,d,blks[i+8],7,1770035416);d=md5ff(d,a,b,c,blks[i+9],12,-1958414417);c=md5ff(c,d,a,b,blks[i+10],17,-42063);b=md5ff(b,c,d,a,blks[i+11],22,-1990404162);
      a=md5ff(a,b,c,d,blks[i+12],7,1804603682);d=md5ff(d,a,b,c,blks[i+13],12,-40341101);c=md5ff(c,d,a,b,blks[i+14],17,-1502002290);b=md5ff(b,c,d,a,blks[i+15],22,1236535329);
      a=md5gg(a,b,c,d,blks[i+1],5,-165796510);d=md5gg(d,a,b,c,blks[i+6],9,-1069501632);c=md5gg(c,d,a,b,blks[i+11],14,643717713);b=md5gg(b,c,d,a,blks[i],20,-373897302);
      a=md5gg(a,b,c,d,blks[i+5],5,-701558691);d=md5gg(d,a,b,c,blks[i+10],9,38016083);c=md5gg(c,d,a,b,blks[i+15],14,-660478335);b=md5gg(b,c,d,a,blks[i+4],20,-405537848);
      a=md5gg(a,b,c,d,blks[i+9],5,568446438);d=md5gg(d,a,b,c,blks[i+14],9,-1019803690);c=md5gg(c,d,a,b,blks[i+3],14,-187363961);b=md5gg(b,c,d,a,blks[i+8],20,1163531501);
      a=md5gg(a,b,c,d,blks[i+13],5,-1444681467);d=md5gg(d,a,b,c,blks[i+2],9,-51403784);c=md5gg(c,d,a,b,blks[i+7],14,1735328473);b=md5gg(b,c,d,a,blks[i+12],20,-1926607734);
      a=md5hh(a,b,c,d,blks[i+5],4,-378558);d=md5hh(d,a,b,c,blks[i+8],11,-2022574463);c=md5hh(c,d,a,b,blks[i+11],16,1839030562);b=md5hh(b,c,d,a,blks[i+14],23,-35309556);
      a=md5hh(a,b,c,d,blks[i+1],4,-1530992060);d=md5hh(d,a,b,c,blks[i+4],11,1272893353);c=md5hh(c,d,a,b,blks[i+7],16,-155497632);b=md5hh(b,c,d,a,blks[i+10],23,-1094730640);
      a=md5hh(a,b,c,d,blks[i+13],4,681279174);d=md5hh(d,a,b,c,blks[i],11,-358537222);c=md5hh(c,d,a,b,blks[i+3],16,-722521979);b=md5hh(b,c,d,a,blks[i+6],23,76029189);
      a=md5hh(a,b,c,d,blks[i+9],4,-640364487);d=md5hh(d,a,b,c,blks[i+12],11,-421815835);c=md5hh(c,d,a,b,blks[i+15],16,530742520);b=md5hh(b,c,d,a,blks[i+2],23,-995338651);
      a=md5ii(a,b,c,d,blks[i],6,-198630844);d=md5ii(d,a,b,c,blks[i+7],10,1126891415);c=md5ii(c,d,a,b,blks[i+14],15,-1416354905);b=md5ii(b,c,d,a,blks[i+5],21,-57434055);
      a=md5ii(a,b,c,d,blks[i+12],6,1700485571);d=md5ii(d,a,b,c,blks[i+3],10,-1894986606);c=md5ii(c,d,a,b,blks[i+10],15,-1051523);b=md5ii(b,c,d,a,blks[i+1],21,-2054922799);
      a=md5ii(a,b,c,d,blks[i+8],6,1873313359);d=md5ii(d,a,b,c,blks[i+15],10,-30611744);c=md5ii(c,d,a,b,blks[i+6],15,-1560198380);b=md5ii(b,c,d,a,blks[i+13],21,1309151649);
      a=md5ii(a,b,c,d,blks[i+4],6,-145523070);d=md5ii(d,a,b,c,blks[i+11],10,-1120210379);c=md5ii(c,d,a,b,blks[i+2],15,718787259);b=md5ii(b,c,d,a,blks[i+9],21,-343485551);
      a=safeAdd(a,aa);b=safeAdd(b,bb);c=safeAdd(c,cc);d=safeAdd(d,dd);
    }
    return [a,b,c,d];
  }
  const nums=strToMD5(str);
  return nums.map(n=>{
    let h="";for(let i=0;i<4;i++)h+=("0"+((n>>(i*8+4))&0xF).toString(16)).slice(-1)+("0"+((n>>(i*8))&0xF).toString(16)).slice(-1);
    return h;
  }).join("");
}

// Morse code
const MORSE_MAP = {
  "a":".-","b":"-...","c":"-.-.","d":"-..","e":".","f":"..-.","g":"--.","h":"....","i":"..","j":".---",
  "k":"-.-","l":".-..","m":"--","n":"-.","o":"---","p":".--.","q":"--.-","r":".-.","s":"...","t":"-",
  "u":"..-","v":"...-","w":".--","x":"-..-","y":"-.--","z":"--..",
  "0":"-----","1":".----","2":"..---","3":"...--","4":"....-","5":".....","6":"-....","7":"--...","8":"---..","9":"----.",
  " ":"/"
};
const MORSE_REVERSE = Object.fromEntries(Object.entries(MORSE_MAP).map(([k,v])=>[v,k]));

const encodeMorse = text =>
  text.toLowerCase().split("").map(c => MORSE_MAP[c] || "?").join(" ");

const decodeMorse = text =>
  text.trim().split(/\s+\/\s+|\s*\/\s*/).map(word =>
    word.trim().split(/\s+/).map(code => MORSE_REVERSE[code] || "?").join("")
  ).join(" ");

// ─── SHARED UI COMPONENTS ─────────────────────────────────────────────────────

function CopyBtn({ text, t, size = "sm" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <button onClick={copy} className={`btn-sm ${copied ? "" : ""}`}
      style={{ borderColor: copied ? t.statusOk : t.border, color: copied ? t.statusOk : t.textMuted, padding: size === "lg" ? "6px 18px" : "3px 10px" }}>
      {copied ? "✓ Copied" : "⎘ Copy"}
    </button>
  );
}

function EncodePanel({ input, output, error, onInput, onSwap, inputLabel, outputLabel, inputPlaceholder, outputPlaceholder, t, children, outputMono = true }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {children}
      <div className="grid-2">
        {/* Input */}
        <div className="panel" style={{ border:`1px solid ${input.trim() ? t.borderActive : t.border}` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{inputLabel}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button className="btn-sm" onClick={() => onInput("")}>Clear</button>
            </div>
          </div>
          <textarea value={input} onChange={e => onInput(e.target.value)} placeholder={inputPlaceholder} spellCheck={false}
            style={{ width:"100%", height:200, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
          <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color: input.trim() ? t.statusOk : t.textMuted }}>
            {input.length} chars
          </div>
        </div>

        {/* Output */}
        <div className="panel active-panel">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
            <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{outputLabel}</span>
            <div style={{ display:"flex", gap:6 }}>
              {onSwap && <button className="btn-sm" onClick={onSwap} title="Use output as input">⇄ Swap</button>}
              {output && <CopyBtn text={output} t={t} />}
            </div>
          </div>
          <textarea readOnly value={error ? "" : output} placeholder={error ? "" : outputPlaceholder} spellCheck={false}
            style={{ width:"100%", height:200, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color: t.textSecondary, fontFamily: outputMono ? "JetBrains Mono, monospace" : "'Plus Jakarta Sans', sans-serif", fontSize:12, lineHeight:1.75, resize:"none" }} />
          <div style={{ padding:"6px 14px", borderTop:`1px solid ${t.border}`, fontSize:11, fontFamily:"JetBrains Mono", color: error ? t.statusErr : output ? t.statusOk : t.textMuted }}>
            {error ? `✕ ${error}` : output ? `${output.length} chars` : "Output appears here"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── INDIVIDUAL TOOL PANELS ───────────────────────────────────────────────────

function Base64Encoder({ t }) {
  const [input, setInput] = useState("Hello, miniDevToolkit! 🚀");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: encodeBase64(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Plain Text" outputLabel="Base64 Encoded" inputPlaceholder="Type or paste text to encode…" outputPlaceholder="Base64 output…" t={t} />;
}

function Base64Decoder({ t }) {
  const [input, setInput] = useState("SGVsbG8sIG1pbmlEZXZUb29sa2l0ISA=");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: decodeBase64(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Base64 String" outputLabel="Decoded Text" inputPlaceholder="Paste Base64 string to decode…" outputPlaceholder="Decoded text…" t={t} outputMono={false} />;
}

function Base64Image({ t }) {
  const [input, setInput] = useState("");
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(null);
  const fileRef = useRef(null);

  const preview = useCallback(() => {
    setImgError(null);
    const str = input.trim();
    if (!str) { setImgSrc(null); return; }
    const src = str.startsWith("data:") ? str : `data:image/png;base64,${str}`;
    setImgSrc(src);
  }, [input]);

  const handleFile = file => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => setInput(e.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
        <button className="btn-sm" onClick={() => fileRef.current?.click()}>📁 Load Image File</button>
        <button className="btn-sm" onClick={preview}>👁 Preview</button>
        <button className="btn-sm" onClick={() => { setInput(""); setImgSrc(null); setImgError(null); }}>Clear</button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e => handleFile(e.target.files[0])} />
      </div>

      <div className="panel" style={{ border:`1px solid ${input.trim() ? t.borderActive : t.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Base64 Image String</span>
          {input && <CopyBtn text={input} t={t} />}
        </div>
        <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Paste a Base64 image string here, or load an image file above…" spellCheck={false}
          style={{ width:"100%", height:140, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:11, lineHeight:1.75, resize:"none" }} />
      </div>

      {imgSrc && (
        <div className="panel active-panel">
          <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
            Image Preview
          </div>
          <div style={{ padding:"16px", display:"flex", justifyContent:"center", background:t.bgInput }}>
            <img src={imgSrc} alt="Base64 preview" onError={() => { setImgError("Could not render image — invalid Base64 or unsupported format"); setImgSrc(null); }}
              style={{ maxWidth:"100%", maxHeight:400, borderRadius:8, display:"block" }} />
          </div>
        </div>
      )}
      {imgError && <div style={{ padding:"10px 14px", background:t.borderError, border:`1px solid ${t.statusErr}`, borderRadius:10, fontSize:13, color:t.statusErr }}>{imgError}</div>}
    </div>
  );
}

function URLEncoder({ t }) {
  const [input, setInput] = useState("https://example.com/search?q=hello world&lang=en");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: encodeURL(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Raw Text / URL" outputLabel="URL Encoded" inputPlaceholder="Paste URL or text to encode…" outputPlaceholder="Encoded URL…" t={t} />;
}

function URLDecoder({ t }) {
  const [input, setInput] = useState("https%3A%2F%2Fexample.com%2Fsearch%3Fq%3Dhello%20world%26lang%3Den");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: decodeURL(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="URL Encoded String" outputLabel="Decoded URL" inputPlaceholder="Paste URL-encoded string to decode…" outputPlaceholder="Decoded URL…" t={t} outputMono={false} />;
}

function HTMLEncoder({ t }) {
  const [input, setInput] = useState('<h1>Hello & Welcome!</h1>\n<script>alert("XSS")</script>');
  const output = useMemo(() => input ? encodeHTML(input) : "", [input]);
  return <EncodePanel input={input} output={output} error={null} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Raw HTML / Text" outputLabel="HTML Encoded" inputPlaceholder="Paste HTML to encode entities…" outputPlaceholder="Encoded HTML…" t={t} />;
}

function HTMLDecoder({ t }) {
  const [input, setInput] = useState("&lt;h1&gt;Hello &amp; Welcome!&lt;/h1&gt;");
  const output = useMemo(() => input ? decodeHTML(input) : "", [input]);
  return <EncodePanel input={input} output={output} error={null} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="HTML Encoded String" outputLabel="Decoded HTML" inputPlaceholder="Paste HTML entities to decode…" outputPlaceholder="Decoded HTML…" t={t} outputMono={false} />;
}

function JWTDecoder({ t }) {
  const [input, setInput] = useState("eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VyXzEyMyIsIm5hbWUiOiJBbGljZSBDaGVuIiwiaWF0IjoxNzAwMDAwMDAwLCJleHAiOjk5OTk5OTk5OTl9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c");
  const result = useMemo(() => {
    if (!input.trim()) return null;
    try { return { data: decodeJWT(input), error: null }; }
    catch(e) { return { data: null, error: e.message }; }
  }, [input]);

  const formatDate = ts => ts ? new Date(ts * 1000).toLocaleString() : "—";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {/* Input */}
      <div className="panel" style={{ border:`1px solid ${input.trim() ? t.borderActive : t.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>JWT Token</span>
          <button className="btn-sm" onClick={() => {}}>Clear</button>
        </div>
        <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Paste JWT token here (header.payload.signature)…" spellCheck={false}
          style={{ width:"100%", height:90, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:11, lineHeight:1.75, resize:"none", wordBreak:"break-all" }} />
      </div>

      {result?.error && (
        <div style={{ padding:"10px 14px", background:t.borderError, border:`1px solid ${t.statusErr}`, borderRadius:10, fontSize:13, color:t.statusErr }}>✕ {result.error}</div>
      )}

      {result?.data && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {/* Status banner */}
          <div style={{ background: result.data.expired ? t.borderError : t.pillGreen, border:`1px solid ${result.data.expired ? t.statusErr : t.statusOk}`, borderRadius:10, padding:"10px 16px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{result.data.expired ? "⚠" : "✓"}</span>
            <div>
              <div style={{ fontSize:14, fontWeight:600, color: result.data.expired ? t.statusErr : t.statusOk }}>
                {result.data.expired === null ? "Token decoded (no expiry)" : result.data.expired ? "Token EXPIRED" : "Token valid"}
              </div>
              {result.data.data?.exp && (
                <div style={{ fontSize:12, color: result.data.expired ? t.statusErr : t.statusOk, opacity:0.8, fontFamily:"JetBrains Mono" }}>
                  Expires: {formatDate(result.data.payload?.exp)}
                </div>
              )}
            </div>
          </div>

          <div className="grid-2">
            {/* Header */}
            <div className="panel">
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
                <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Header</span>
                <CopyBtn text={JSON.stringify(result.data.header, null, 2)} t={t} />
              </div>
              <pre style={{ padding:"12px 14px", margin:0, fontSize:12, fontFamily:"JetBrains Mono, monospace", color:t.textSecondary, lineHeight:1.75, overflow:"auto", background:"transparent" }}>
                {JSON.stringify(result.data.header, null, 2)}
              </pre>
            </div>

            {/* Payload */}
            <div className="panel">
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
                <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Payload</span>
                <CopyBtn text={JSON.stringify(result.data.payload, null, 2)} t={t} />
              </div>
              <pre style={{ padding:"12px 14px", margin:0, fontSize:12, fontFamily:"JetBrains Mono, monospace", color:t.textSecondary, lineHeight:1.75, overflow:"auto", background:"transparent" }}>
                {JSON.stringify(result.data.payload, null, 2)}
              </pre>
            </div>
          </div>

          {/* Claims table */}
          <div className="panel">
            <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
              Standard Claims
            </div>
            <div style={{ padding:"12px 14px", display:"grid", gridTemplateColumns:"auto 1fr", gap:"8px 20px", fontSize:12 }}>
              {[
                { key:"sub",  label:"Subject",   val: result.data.payload?.sub },
                { key:"iss",  label:"Issuer",    val: result.data.payload?.iss },
                { key:"aud",  label:"Audience",  val: result.data.payload?.aud },
                { key:"iat",  label:"Issued At", val: result.data.payload?.iat ? formatDate(result.data.payload.iat) : null },
                { key:"exp",  label:"Expires",   val: result.data.payload?.exp ? formatDate(result.data.payload.exp) : null },
                { key:"nbf",  label:"Not Before",val: result.data.payload?.nbf ? formatDate(result.data.payload.nbf) : null },
                { key:"alg",  label:"Algorithm", val: result.data.header?.alg },
                { key:"typ",  label:"Type",      val: result.data.header?.typ },
              ].filter(r => r.val !== undefined && r.val !== null).map(row => (
                <div key={row.key} style={{ display:"contents" }}>
                  <span style={{ color:t.textMuted, fontFamily:"JetBrains Mono", whiteSpace:"nowrap" }}>{row.label}</span>
                  <span style={{ color:t.textPrimary, fontFamily:"JetBrains Mono" }}>{String(row.val)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Signature */}
          <div className="panel">
            <div style={{ padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}`, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>
              Signature (raw — not verified)
            </div>
            <div style={{ padding:"12px 14px", fontFamily:"JetBrains Mono", fontSize:11, color:t.textMuted, wordBreak:"break-all" }}>
              {result.data.signature}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HexEncoder({ t }) {
  const [input, setInput] = useState("Hello, World!");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: encodeHex(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Plain Text" outputLabel="Hex Encoded" inputPlaceholder="Type or paste text to encode to hex…" outputPlaceholder="48 65 6c 6c 6f…" t={t} />;
}

function HexDecoder({ t }) {
  const [input, setInput] = useState("48 65 6c 6c 6f 2c 20 57 6f 72 6c 64 21");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: decodeHex(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Hex String" outputLabel="Decoded Text" inputPlaceholder="Paste hex bytes (e.g. 48 65 6c 6c 6f)…" outputPlaceholder="Decoded text…" t={t} outputMono={false} />;
}

function UnicodeEncoder({ t }) {
  const [input, setInput] = useState("Hello © World ™ 你好");
  const output = useMemo(() => input ? encodeUnicode(input) : "", [input]);
  return <EncodePanel input={input} output={output} error={null} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Plain Text" outputLabel="Unicode Escaped" inputPlaceholder="Type text with special characters…" outputPlaceholder="\u0048\u0065\u006c\u006c\u006f…" t={t} />;
}

function UnicodeDecoder({ t }) {
  const [input, setInput] = useState("\\u0048\\u0065\\u006c\\u006c\\u006f \\u00a9 \\u4e16\\u754c");
  const { output, error } = useMemo(() => {
    if (!input) return { output:"", error:null };
    try { return { output: decodeUnicode(input), error:null }; }
    catch(e) { return { output:"", error: e.message }; }
  }, [input]);
  return <EncodePanel input={input} output={output} error={error} onInput={setInput} onSwap={() => { if(output) setInput(output); }} inputLabel="Unicode Escaped String" outputLabel="Decoded Text" inputPlaceholder="Paste \\uXXXX sequences to decode…" outputPlaceholder="Decoded text…" t={t} outputMono={false} />;
}

function HashGenerator({ t }) {
  const [input, setInput] = useState("Hello, World!");
  const [sha256, setSha256] = useState("");
  const [md5Hash, setMd5Hash] = useState("");
  const [loading, setLoading] = useState(false);

  const compute = useCallback(async () => {
    if (!input.trim()) { setSha256(""); setMd5Hash(""); return; }
    setLoading(true);
    try {
      const [sha, m] = await Promise.all([
        hashSHA256(input),
        Promise.resolve(md5(input)),
      ]);
      setSha256(sha);
      setMd5Hash(m);
    } finally { setLoading(false); }
  }, [input]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ background:t.bgChipActive, border:`1px solid ${t.border}`, borderRadius:10, padding:"10px 14px", fontSize:13, color:t.textSecondary, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:16 }}>ℹ</span>
        Hash functions are one-way only — you cannot decode a hash back to the original text.
      </div>

      <div className="panel" style={{ border:`1px solid ${input.trim() ? t.borderActive : t.border}` }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
          <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>Input Text</span>
          <button className="btn-sm" onClick={() => { setInput(""); setSha256(""); setMd5Hash(""); }}>Clear</button>
        </div>
        <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Type or paste text to hash…" spellCheck={false}
          style={{ width:"100%", height:140, background:"transparent", border:"none", outline:"none", padding:"12px 14px", color:t.textPrimary, fontFamily:"JetBrains Mono, monospace", fontSize:12, lineHeight:1.75, resize:"none" }} />
        <div style={{ padding:"8px 14px", borderTop:`1px solid ${t.border}` }}>
          <button className="btn-primary" onClick={compute} disabled={!input.trim() || loading} style={{ padding:"7px 20px", fontSize:12 }}>
            {loading ? "⏳ Computing…" : "# Compute Hashes"}
          </button>
        </div>
      </div>

      {(sha256 || md5Hash) && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            { label:"MD5",    val: md5Hash, bits:"128-bit" },
            { label:"SHA-256",val: sha256,  bits:"256-bit" },
          ].map(({ label, val, bits }) => (
            <div key={label} className="panel">
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:t.bgPanelHead, borderBottom:`1px solid ${t.border}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textAccent }}>{label}</span>
                  <span className="pill pill-blue" style={{ fontSize:9 }}>{bits}</span>
                </div>
                {val && <CopyBtn text={val} t={t} />}
              </div>
              <div style={{ padding:"12px 14px", fontFamily:"JetBrains Mono", fontSize:12, color:t.textSecondary, wordBreak:"break-all", letterSpacing:"0.05em" }}>
                {val || <span style={{ color:t.textMuted }}>—</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MorseCode({ t }) {
  const [input, setInput]   = useState("Hello World");
  const [mode, setMode]     = useState("encode"); // encode | decode
  const { output, error } = useMemo(() => {
    if (!input.trim()) return { output:"", error:null };
    try {
      const out = mode === "encode" ? encodeMorse(input) : decodeMorse(input);
      return { output: out, error:null };
    } catch(e) { return { output:"", error: e.message }; }
  }, [input, mode]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      <div style={{ display:"flex", gap:6 }}>
        {[{id:"encode",label:"Text → Morse"},{id:"decode",label:"Morse → Text"}].map(m => (
          <button key={m.id} className="btn-sm" onClick={() => { setMode(m.id); setInput(""); }}
            style={{ borderColor: mode===m.id ? t.borderActive : t.border, color: mode===m.id ? t.textAccent : t.textMuted, background: mode===m.id ? t.bgChipActive : "none", padding:"5px 14px" }}>
            {m.label}
          </button>
        ))}
      </div>
      <EncodePanel
        input={input} output={output} error={error}
        onInput={setInput}
        onSwap={() => { if(output) { setInput(output); setMode(m => m==="encode" ? "decode" : "encode"); }}}
        inputLabel={mode==="encode" ? "Plain Text" : "Morse Code"}
        outputLabel={mode==="encode" ? "Morse Code" : "Plain Text"}
        inputPlaceholder={mode==="encode" ? "Type text to convert to Morse…" : "Paste Morse code (use / for spaces)…"}
        outputPlaceholder={mode==="encode" ? "•— —••• •••" : "Decoded text…"}
        t={t}
        outputMono={mode==="encode"}
      />
      <div style={{ background:t.bgPanel, border:`1px solid ${t.border}`, borderRadius:10, padding:"12px 14px" }}>
        <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", color:t.textMuted, marginBottom:8 }}>Morse Reference</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(80px,1fr))", gap:4, fontSize:11, fontFamily:"JetBrains Mono" }}>
          {Object.entries(MORSE_MAP).filter(([k]) => k !== " ").slice(0, 36).map(([char, code]) => (
            <div key={char} style={{ display:"flex", gap:6, color:t.textMuted }}>
              <span style={{ color:t.textAccent, fontWeight:600, minWidth:14 }}>{char.toUpperCase()}</span>
              <span>{code}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function EncodingToolkit({ t }) {
  const [activeTool, setActiveTool] = useState("base64-enc");

  const toolComponents = {
    "base64-enc":  <Base64Encoder t={t} />,
    "base64-dec":  <Base64Decoder t={t} />,
    "base64-img":  <Base64Image t={t} />,
    "url-enc":     <URLEncoder t={t} />,
    "url-dec":     <URLDecoder t={t} />,
    "html-enc":    <HTMLEncoder t={t} />,
    "html-dec":    <HTMLDecoder t={t} />,
    "jwt":         <JWTDecoder t={t} />,
    "hex-enc":     <HexEncoder t={t} />,
    "hex-dec":     <HexDecoder t={t} />,
    "unicode-enc": <UnicodeEncoder t={t} />,
    "unicode-dec": <UnicodeDecoder t={t} />,
    "hash":        <HashGenerator t={t} />,
    "morse":       <MorseCode t={t} />,
  };

  const activeDef = TOOLS.find(tt => tt.id === activeTool);

  return (
    <div>
      {/* Hero */}
      <div className="tool-hero fade-up">
        <div className="tool-hero-inner">
          <div className="tool-hero-left">
            <h2>🔐 Encoding / Decoding Toolkit</h2>
            <p>14 encoding, decoding, and hashing tools in one place — Base64, URL, HTML, JWT, Hex, Unicode, Hash & Morse.</p>
          </div>
          <div className="tool-hero-stats">
            <div className="tool-stat"><div className="tool-stat-val">14</div><div className="tool-stat-lbl">tools</div></div>
            <div className="tool-stat"><div className="tool-stat-val">{GROUPS.length}</div><div className="tool-stat-lbl">categories</div></div>
            <div className="tool-stat"><div className="tool-stat-val">0</div><div className="tool-stat-lbl">server calls</div></div>
          </div>
        </div>
      </div>

      {/* Sub-tool tabs grouped by category */}
      <div className="pill-tab-bar" style={{ display:"flex", gap:6, padding:"12px 16px", flexWrap:"wrap", overflowX:"auto", scrollbarWidth:"none" }}>
        {GROUPS.map(group => (
          <div key={group} style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
            <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:t.textMuted, whiteSpace:"nowrap", paddingRight:2 }}>{group}:</span>
            {TOOLS.filter(tt => tt.group === group).map(tool => {
              const active = activeTool === tool.id;
              return (
                <button key={tool.id} onClick={() => setActiveTool(tool.id)}
                  className={`pill-tab ${active ? "active" : ""}`} style={{ fontSize:11 }}>
                  <span style={{ fontSize:11, fontFamily:"JetBrains Mono" }}>{tool.icon}</span>
                  {tool.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Active tool description */}
      {activeDef && (
        <div style={{ maxWidth:1200, margin:"0 auto", padding:"1rem 1.5rem 0", display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18 }}>{activeDef.icon}</span>
          <div>
            <span style={{ fontSize:14, fontWeight:600, color:t.textPrimary }}>{activeDef.label}</span>
            <span style={{ fontSize:12, color:t.textMuted, marginLeft:10 }}>{activeDef.desc}</span>
          </div>
        </div>
      )}

      {/* Tool content */}
      <div className="tool-main">
        {toolComponents[activeTool]}
      </div>

      <article className="tool-seo-section" aria-label="About Encoding Toolkit">
        <h2>About Encoding Toolkit</h2>
        <div className="tool-seo-cols">
          <div>
            <h3>How it works</h3>
            <ul>
              <li>Base64 encodes/decodes text using the browser's built-in <code>btoa</code>/<code>atob</code> with UTF-8 support.</li>
              <li>JWT decoding parses the header and payload without needing the secret key.</li>
              <li>URL encoding uses <code>encodeURIComponent</code> for RFC 3986 compliance.</li>
              <li>All 14 tools process your input entirely client-side.</li>
            </ul>
          </div>
          <div>
            <h3>Use cases</h3>
            <ul>
              <li>Inspect JWT tokens from API responses without a backend.</li>
              <li>Encode image files to Base64 for CSS or HTML embedding.</li>
              <li>Safely encode URL parameters before appending them to requests.</li>
              <li>Encode HTML content to prevent XSS in email templates.</li>
            </ul>
          </div>
          <div>
            <h3>Privacy</h3>
            <ul>
              <li>All encoding and decoding runs in your browser.</li>
              <li>JWT tokens and sensitive strings are never transmitted.</li>
              <li>No sign-up, no account required.</li>
            </ul>
          </div>
        </div>
        <h2 style={{ marginTop:"1.5rem" }}>Frequently Asked Questions</h2>
        <details><summary>Can I decode a JWT without the secret key?</summary><p>Yes. JWT tokens consist of three Base64url-encoded parts: header, payload, and signature. The JWT Decoder shows the header and payload (including all claims like sub, iss, exp, iat, nbf, aud) without needing the secret key. However, it cannot verify the signature without the key.</p></details>
        <details><summary>What is the difference between Base64 and Base64 URL encoding?</summary><p>Standard Base64 uses + and / characters and may include = padding. Base64 URL encoding replaces + with - and / with _, and omits padding, making it safe to use in URLs and HTTP headers without percent-encoding.</p></details>
        <details><summary>Is Morse code encoding bidirectional?</summary><p>Yes. The Morse Code tool encodes text (A–Z, 0–9, common punctuation) to dots and dashes, and decodes dot-dash patterns back to text. Use a space between letters and a slash (/) between words when decoding.</p></details>
        <details><summary>Does the Encoding Toolkit work without an internet connection?</summary><p>Yes. All 14 encoding and decoding operations run entirely in your browser. After the first load, the Encoding Toolkit works offline — no network requests are made when you encode or decode data.</p></details>
        <h2 style={{ marginTop:"1.5rem" }}>Alternatives &amp; why miniDevToolkit</h2>
        <p>Compared to <strong>CyberChef</strong> — miniDevToolkit offers a simpler, task-focused UI: each encoding operation is a dedicated tab rather than a recipe you must configure. For Base64, JWT, URL, and Hex tasks, miniDevToolkit is faster to reach and easier to use. Compared to <strong>Base64.guru</strong> — miniDevToolkit bundles 14 tools instead of one, with the same privacy guarantee. Compared to <strong>dcode.fr</strong> — miniDevToolkit adds JWT decoding with expiry checking, file hashing, and a full JSON toolkit alongside the encoding tools. All processing is client-side and verifiable via DevTools.</p>
      </article>
    </div>
  );
}